import { cva, type VariantProps } from 'class-variance-authority'
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Aviso dentro de uma tela (erro de formulário, alerta de segurança, dica).
 *
 * Nenhuma variante usa o amarelo da marca: aqui amarelo seria confundido com
 * identidade. Aviso é âmbar (`--warning-fg`) e SEMPRE com ícone — a regra do
 * CLAUDE.md de que estado nunca depende só de cor vale também para texto.
 */
const alertVariants = cva(
  cn(
    'flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-sm',
    '[&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        info: 'border-info-fg/40 bg-info/10 text-info-fg',
        success: 'border-success-fg/40 bg-success/10 text-success-fg',
        warning: 'border-warning-fg/40 bg-warning/10 text-warning-fg',
        danger: 'border-danger-fg/40 bg-danger/10 text-danger-fg',
        neutral: 'border-border bg-elevated text-fg-secondary',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  },
)

const ICONES = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
  neutral: Info,
} as const

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  /** Esconde o ícone quando o contexto já tem um. */
  readonly semIcone?: boolean
}

function Alert({
  className,
  variant,
  semIcone = false,
  children,
  ...props
}: AlertProps): React.ReactElement {
  const Icone = ICONES[variant ?? 'neutral']

  return (
    <div
      // `alert` faz o leitor de tela anunciar na hora — é o comportamento certo
      // para erro de submissão, que é o uso principal deste componente.
      role="alert"
      data-slot="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {semIcone ? null : <Icone aria-hidden="true" />}
      <div className="min-w-0 flex-1 space-y-1">{children}</div>
    </div>
  )
}

function AlertTitle({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>): React.ReactElement {
  return (
    <p className={cn('font-medium leading-tight', className)} {...props}>
      {children}
    </p>
  )
}

function AlertDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>): React.ReactElement {
  return <p className={cn('leading-relaxed opacity-90', className)} {...props} />
}

export { Alert, AlertDescription, alertVariants, AlertTitle }
